import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, PencilLine, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Markdown from "@/components/blog/Markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", slug: "", category: "", tags: "", target_keyword: "", excerpt: "", meta_description: "", content: "", status: "draft" };
const toForm = (p) => (p ? { ...EMPTY, ...p, tags: (p.tags || []).join(", "), target_keyword: p.target_keyword || "", meta_description: p.meta_description || "" } : EMPTY);
const inputCls = "mt-1.5 w-full border border-slate-300 focus:border-topo outline-none px-3 py-2 text-sm rounded-sm bg-white";

function F({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mono text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

/** Create / edit a blog post with markdown preview. */
export default function BlogEditorDialog({ open, post, categories, token, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm(toForm(post)); setPreview(false); } }, [open, post]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      slug: form.slug.trim() || undefined,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      target_keyword: form.target_keyword.trim() || undefined,
      meta_description: form.meta_description.trim() || undefined,
    };
    delete payload.id; delete payload.read_minutes; delete payload.published_at; delete payload.created_at; delete payload.updated_at; delete payload.seeded;
    try {
      const headers = { "X-Admin-Token": token };
      const res = post ? await axios.put(`${API}/admin/blog/posts/${post.id}`, payload, { headers }) : await axios.post(`${API}/admin/blog/posts`, payload, { headers });
      toast.success(post ? "Post updated." : "Post created.");
      onSaved(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Check "${d[0]?.loc?.slice(-1)[0]}": ${d[0]?.msg}` : d || "Could not save the post.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="blog-editor">
        <DialogHeader>
          <DialogTitle className="display text-2xl font-extrabold tracking-tight">{post ? "Edit post" : "New post"}</DialogTitle>
          <DialogDescription>Write in Markdown. Use ## for section headings; links starting with / stay inside the site.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><F label="Title"><input value={form.title} onChange={set("title")} className={inputCls} data-testid="blog-editor-title" /></F></div>
          <F label="Slug" hint="Leave blank to generate from the title"><input value={form.slug} onChange={set("slug")} className={inputCls} data-testid="blog-editor-slug" placeholder="how-much-does-it-cost" /></F>
          <F label="Category">
            <input value={form.category} onChange={set("category")} list="blog-categories" className={inputCls} data-testid="blog-editor-category" />
            <datalist id="blog-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </F>
          <F label="Target keyword" hint="Shown in the keyword tracker"><input value={form.target_keyword} onChange={set("target_keyword")} className={inputCls} data-testid="blog-editor-keyword" /></F>
          <F label="Tags" hint="Comma separated"><input value={form.tags} onChange={set("tags")} className={inputCls} data-testid="blog-editor-tags" /></F>
          <div className="md:col-span-2"><F label="Excerpt"><textarea rows={2} value={form.excerpt} onChange={set("excerpt")} className={inputCls} data-testid="blog-editor-excerpt" /></F></div>
          <div className="md:col-span-2"><F label="Meta description" hint={`${form.meta_description.length}/300 · shown in Google results`}><textarea rows={2} value={form.meta_description} onChange={set("meta_description")} className={inputCls} data-testid="blog-editor-meta" /></F></div>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <span className="mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Content</span>
            <button type="button" onClick={() => setPreview((v) => !v)} data-testid="blog-editor-preview-toggle" className="inline-flex items-center gap-1.5 text-xs font-semibold text-topo">
              {preview ? <><PencilLine className="h-3.5 w-3.5" /> Edit</> : <><Eye className="h-3.5 w-3.5" /> Preview</>}
            </button>
          </div>
          {preview
            ? <div className="mt-1.5 border border-slate-200 p-5 max-h-[420px] overflow-y-auto"><Markdown content={form.content || "_Nothing to preview yet._"} testid="blog-editor-preview" /></div>
            : <textarea rows={16} value={form.content} onChange={set("content")} className={`${inputCls} mono text-[13px] leading-relaxed`} data-testid="blog-editor-content" />}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
          <select value={form.status} onChange={set("status")} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="blog-editor-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published</option>
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-sm border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid="blog-editor-cancel">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold" data-testid="blog-editor-save">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save post"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
